import React, {useState} from 'react';
import ReactModal from "react-modal";
import { useForm } from 'react-hook-form';
import {useLocation} from 'react-router-dom';
import {ApiClientError} from "../../../../apis/errors";
import {ValidationErrorMessages} from "../../common/ValidationErrorMessages";
import {FormValidationType, makeRegisterOptions} from "../../../../libs/reactHookForm/makeRegisterOptions";
import useLoading from '../../../../hooks/useLoading';
import {roomCustomerApi} from '../../../../apis/admin/room/roomCustomerApi';
import {logError} from '../../../../libs/utils/logError';
import useAppRouter from '../../../../hooks/useAppRouter';
import {ConfirmAddCustomerRoomMemberModal} from './ConfirmAddCustomerRoomMemberModal';
import {Customer} from '../../../../types/admin/room';
import {roomMemberApi} from '../../../../apis/admin/room/roomMemberApi';
import {CreateCustomerModal} from '../Account/CreateCustomerModal';

interface AddCustomerRoomMemberModalProps {
  roomId: number;
  selectedCustomerEmails: string[];
  onClosed: () => Promise<void>;
  onAdded: () => Promise<void>;
}

interface FormValues {
  email: string;
}

export const AddCustomerRoomMemberModal = (props: AddCustomerRoomMemberModalProps) => {
  const { roomId, selectedCustomerEmails, onClosed, onAdded } = props;

  const {
    register,
    formState: { errors, },
    watch,
    setError,
    handleSubmit
  } = useForm({
    reValidateMode: 'onSubmit',
  });
  const withLoading = useLoading();
  const location = useLocation();
  const { goto } = useAppRouter();

  const [ apiClientError, setApiClientError ] = useState<ApiClientError | null>(null);
  const [ customer, setCustomer ] = useState<Customer | null>(null);
  const [
    sowConfirmAddCustomerRoomMemberModal,
    setShowConfirmAddCustomerRoomMemberModal
  ] = useState<boolean>(false);
  const [showCreateCustomerModal, setShowCreateCustomerModal] = useState<boolean>(false);

  const handleSearchButtonClick = () => {
    withLoading(async () => {
      setApiClientError(null);

      await handleSubmit(async (formValues: FormValues) => {
        try {
          if (selectedCustomerEmails.includes(formValues.email)) {
            setError('email', {
              type: 'already_selected',
              message: 'このメールアドレスの顧客ユーザーは選択済みです',
            });
            return;
          }

          const response = await roomCustomerApi.search({email: formValues.email}, {});
          if (response.isExist) {
            setCustomer(response.customer);
            setShowConfirmAddCustomerRoomMemberModal(true);
          } else {
            setShowCreateCustomerModal(true);
          }

        } catch (e) {
          await logError(
            location.pathname,
            '顧客ユーザーの検索に失敗しました。',
            e
          );

          if (e instanceof ApiClientError) {
            setApiClientError(e);
            return;
          }

          goto('internalServerError');
        }

        await onAdded();
      })();
    });
  }

  const addCustomerToRoomMember = async (addTargetCustomer: Customer) => {
    try {
      await roomMemberApi.addRoomMember({
        roomId,
        userId: addTargetCustomer.userId,
        roleId: 4
      }, {});

    } catch (e) {
      await logError(
        location.pathname,
        '顧客のROOM参加者への追加に失敗しました。',
        e
      );

      if (e instanceof ApiClientError) {
        setApiClientError(e);
        return;
      }

      goto('internalServerError');
    }
  }

  const handleAccepted = async () => {
    if (!customer) {
      throw new Error('顧客のデータがありません。');
    }

    await addCustomerToRoomMember(customer);
    await onClosed();
    await onAdded();
  }

  const handleCreated = async (createdCustomer: Customer) => {
    await addCustomerToRoomMember(createdCustomer);
    await onClosed();
    await onAdded();
  }

  return (
    <>
      {sowConfirmAddCustomerRoomMemberModal && customer && (
        <ConfirmAddCustomerRoomMemberModal
          customer={customer}
          onAccepted={handleAccepted}
          onClosed={async () => setShowConfirmAddCustomerRoomMemberModal(false)}
        />
      )}

      {showCreateCustomerModal && (
        <CreateCustomerModal
          enableSuccessNotification={false}
          infoMessage="未登録の顧客です。データを新規に登録してください。"
          defaultValues={{
            userName: '',
            companyName: '',
            email: watch('email'),
            department: '',
            position: '',
          }}
          onClosed={async () => setShowCreateCustomerModal(false)}
          onCreated={handleCreated}
        />
      )}

      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={onClosed}
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
        className="form-modal"
      >
        <section className="modal-background">
          <div className="form-modal-container">
            <div className="form-modal-title">
              <h1 className="font-semibold">顧客追加</h1>
              <button
                type="button"
                className="modal-close-btn"
                onClick={onClosed}
              >
                ×
              </button>
            </div>

            <form id="searchUserForm">
              <div className="form-modal-content">
                <div className="text-danger text-xs mb-4">
                  { apiClientError?.message }
                  { (Object.keys(errors).length > 0) && '入力エラーがあります' }
                </div>

                <div className="grid grid-cols-1 w-full">
                  <label className="block" htmlFor="ownerEmail">
                    <input
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      {...register('email',
                        makeRegisterOptions('メールアドレス', [
                          { type: FormValidationType.REQUIRED },
                          { type: FormValidationType.MAX_STRING_LENGTH, value: 86 },
                          { type: FormValidationType.EMAIL, typeName: 'email' },
                        ])
                      )}
                      className="text-field"
                      id="email"
                      type="text"
                      placeholder="検索する顧客のメールアドレスを入力してください"
                      maxLength={86}
                    />
                    <div className="mt-1 text-danger text-xs">
                      {errors.email?.message}
                    </div>
                    <ValidationErrorMessages
                      {...{
                        fieldErrors: apiClientError?.errors,
                        field: 'email',
                      }}
                    />
                  </label>
                </div>
              </div>
            </form>

            <div className="form-modal-footer-container">
              <div className="form-modal-footer">
                <button
                  className="float-left btn-sub"
                  type="button"
                  onClick={onClosed}
                >
                  キャンセル
                </button>
                <button
                  className="btn-primary"
                  type="button"
                  onClick={handleSearchButtonClick}
                >
                  検 索
                </button>
              </div>
            </div>
          </div>
        </section>
      </ReactModal>
    </>
  );
};
